/**
 * LoadingStates.tsx
 * 
 * Provides consistent loading states and skeleton loaders for the application
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { ThemeColors, Spacing, BorderRadius } from '../../styles/ThemeStyles';

// Loading animation keyframes
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

// Base skeleton loader
export const SkeletonLoader = styled.div<{ width?: string; height?: string; borderRadius?: string }>`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '20px'};
  border-radius: ${props => props.borderRadius || BorderRadius.md};
  margin-bottom: ${Spacing.sm};
`;

// Card skeleton loader
export const CardSkeleton = styled.div`
  background-color: ${ThemeColors.background.paper};
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: ${Spacing.md};
`;

// Dashboard card skeleton
export const DashboardCardSkeleton: React.FC = () => (
  <CardSkeleton>
    <SkeletonLoader height="120px" borderRadius={BorderRadius.lg} />
    <div style={{ padding: Spacing.md }}>
      <SkeletonLoader height="24px" width="60%" />
      <SkeletonLoader height="16px" width="80%" />
    </div>
  </CardSkeleton>
);

// Table skeleton loader
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 5, 
  columns = 4 
}) => (
  <div>
    {/* Header skeleton */}
    <div style={{ display: 'flex', gap: Spacing.md, marginBottom: Spacing.md }}>
      {Array.from({ length: columns }).map((_, index) => (
        <SkeletonLoader key={index} height="20px" width="120px" />
      ))}
    </div>
    
    {/* Rows skeleton */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} style={{ display: 'flex', gap: Spacing.md, marginBottom: Spacing.sm }}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <SkeletonLoader key={colIndex} height="16px" width="100px" />
        ))}
      </div>
    ))}
  </div>
);

// Spinner component
export const Spinner = styled.div<{ size?: string; color?: string }>`
  width: ${props => props.size || '24px'};
  height: ${props => props.size || '24px'};
  border: 2px solid ${props => props.color || ThemeColors.gray[200]};
  border-top: 2px solid ${props => props.color || ThemeColors.primary.main};
  border-radius: 50%;
  animation: ${pulse} 1s linear infinite;
`;

// Loading overlay
export const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
`;

// Loading component with text
export interface LoadingProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  overlay?: boolean;
}

export const Loading: React.FC<LoadingProps> = ({ 
  size = 'md', 
  text, 
  overlay = false 
}) => {
  const { t } = useTranslation();
  const loadingText = text || t('common.loading', 'Loading...');
  const sizeMap = {
    sm: '16px',
    md: '24px',
    lg: '32px'
  };

  const spinnerSize = sizeMap[size];

  const content = (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      gap: Spacing.md 
    }}>
      <Spinner size={spinnerSize} />
      {text && (
        <span style={{ 
          color: ThemeColors.text.secondary,
          fontSize: size === 'sm' ? '12px' : size === 'lg' ? '16px' : '14px'
        }}>
          {loadingText}
        </span>
      )}
    </div>
  );

  if (overlay) {
    return (
      <LoadingOverlay>
        {content}
      </LoadingOverlay>
    );
  }

  return content;
};

// Progress bar component
export interface ProgressBarProps {
  progress: number; // 0-100
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  showPercentage?: boolean;
}

const ProgressBarContainer = styled.div<{ size: string }>`
  width: 100%;
  background-color: ${ThemeColors.gray[200]};
  border-radius: ${BorderRadius.md};
  overflow: hidden;
  height: ${props => 
    props.size === 'sm' ? '4px' : 
    props.size === 'lg' ? '12px' : 
    '8px'
  };
`;

const ProgressBarFill = styled.div<{ progress: number; color: string }>`
  height: 100%;
  width: ${props => props.progress}%;
  background-color: ${props => props.color};
  transition: width 0.3s ease;
  border-radius: ${BorderRadius.md};
`;

export const ProgressBar: React.FC<ProgressBarProps> = ({ 
  progress, 
  size = 'md', 
  color = ThemeColors.primary.main,
  showPercentage = false 
}) => (
  <div style={{ width: '100%' }}>
    <ProgressBarContainer size={size}>
      <ProgressBarFill progress={progress} color={color} />
    </ProgressBarContainer>
    {showPercentage && (
      <div style={{ 
        textAlign: 'right', 
        marginTop: Spacing.xs,
        fontSize: '12px',
        color: ThemeColors.text.secondary
      }}>
        {Math.round(progress)}%
      </div>
    )}
  </div>
);

// Inline loading component for buttons
export const InlineLoading: React.FC<{ size?: string }> = ({ size = '16px' }) => (
  <Spinner size={size} />
);

// Loading states for different components
export const ComponentLoadingStates = {
  DashboardCard: DashboardCardSkeleton,
  Table: TableSkeleton,
  Spinner,
  Loading,
  ProgressBar,
  InlineLoading
};

export default {
  SkeletonLoader,
  CardSkeleton,
  DashboardCardSkeleton,
  TableSkeleton,
  Spinner,
  Loading,
  ProgressBar,
  InlineLoading,
  ComponentLoadingStates
};
